import React from 'react';
import { useToggle } from 'react-use';
import { connect } from 'react-redux';
import { Icon, Label } from 'semantic-ui-react';
import Parser from 'html-react-parser';
import { StateType } from '../../reducers';
import {
  getCurrentlyViewedStep,
  getIsTutorialModeActiveSelector,
  getMaxUnlockedTutorialStep,
  getTutorialTotalSteps,
} from '../../reducers/TutorialReducer/TutorialSelectors';
import { AlphaTutorialResults } from './AlphaTutorialResults';
import { useAlphaTutorialChecks, useTutorialSteps } from '../../shared/api/apiHooks';
import { ErrorBoundary } from '../../containers';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import './AlphaTutorialNotification.scss';

export interface AlphaTutorialNotificationProps {
  alphaPath: string[];
}

interface AlphaTutorialNotificationStateProps {
  isTutorialModeActive: boolean;
  alphaId: string;
  alphaIsTutorialModeActive: boolean;
  alphaTutorialStep: number | undefined;
  alphaStatus: string;
  currentlyViewedStep: number;
  lastUnlockedTutorialStep: number;
  totalSteps: number;
}

type AlphaTutorialNotificationGenericProps = AlphaTutorialNotificationProps & AlphaTutorialNotificationStateProps;

function AlphaTutorialNotificationImpl({
  isTutorialModeActive,
  alphaId,
  alphaIsTutorialModeActive,
  alphaTutorialStep,
  lastUnlockedTutorialStep,
  currentlyViewedStep,
  totalSteps,
  alphaStatus,
}: AlphaTutorialNotificationGenericProps) {
  const { data: tutorialSteps } = useTutorialSteps();

  const isSubmitted = alphaStatus === 'ACTIVE';
  const { isFetching, data: checkResponse } = useAlphaTutorialChecks({ alphaId: alphaId, isSubmitted: isSubmitted });

  const [isOpened, toggleIsOpened] = useToggle(true);
  const slugIndex = tutorialSteps?.findIndex(step => step.slug === checkResponse?.step) ?? -1;
  const currentStepIndex = slugIndex >= 0 ? slugIndex : lastUnlockedTutorialStep;

  const currentStep = tutorialSteps?.[currentStepIndex];
  const currentTutorialName = currentStep?.name ?? 'loading...';
  const currentStepName = currentStep?.task ?? 'loading...';
  const nextTutorialName = tutorialSteps?.[currentStepIndex + 1]?.name ?? 'loading...';

  const isNotificationShown = // The notification was closed manually
    isOpened &&
    isTutorialModeActive &&
    alphaIsTutorialModeActive &&
    lastUnlockedTutorialStep !== totalSteps &&
    typeof alphaTutorialStep === 'number' &&
    (lastUnlockedTutorialStep === alphaTutorialStep ||
      (lastUnlockedTutorialStep === alphaTutorialStep + 1 && currentlyViewedStep === alphaTutorialStep));

  if (!isNotificationShown) {
    return null;
  }

  return (
    <ErrorBoundary isNotFullScreen={true}>
      <div className="tutorial-notification alphas-details-content__box">
        <div className="tutorial-notification__top-section">
          <Label className="mode-label" color="green" size="tiny">
            <FormattedMessageHelper id="tutorialNotification.modeLabel" />
          </Label>
          <button
            className="tutorial-notification__header-close"
            onClick={toggleIsOpened}
            data-testid="tutorial-close-button"
            aria-label="Close tutorial notification"
          >
            <span className="tutorial-notification__header-close-icon" />
          </button>
        </div>

        <div className="tutorial-notification__explain-info">
          <Icon name="info circle" color="blue" />
          <FormattedMessageHelper id="tutorialNotification.infoExplanation" />
        </div>

        <h3 className="tutorial-notification__tutorial-name">
          <Icon name="book" color="green" />
          <FormattedMessageHelper id="tutorialNotification.header" values={{ tutorialName: currentTutorialName }} />
        </h3>

        <p>{Parser(currentStepName)}</p>

        <AlphaTutorialResults
          nextTutorialName={nextTutorialName}
          stepIndex={alphaTutorialStep}
          isFetching={isFetching}
          checkResponse={checkResponse}
        />
      </div>
    </ErrorBoundary>
  );
}

const mapStateToProps = (state: StateType, ownProps: AlphaTutorialNotificationProps): AlphaTutorialNotificationStateProps => ({
  isTutorialModeActive: getIsTutorialModeActiveSelector(state),
  alphaId: state.getIn(ownProps.alphaPath.concat(['alphaDetail', 'id'])) as string,
  alphaIsTutorialModeActive: state.getIn(ownProps.alphaPath.concat(['isTutorialModeActive'])) as boolean,
  alphaTutorialStep: state.getIn(ownProps.alphaPath.concat(['tutorialStep'])) as number | undefined,
  alphaStatus: state.getIn(ownProps.alphaPath.concat(['alphaDetail', 'status'])) as string,

  lastUnlockedTutorialStep: getMaxUnlockedTutorialStep(state),
  totalSteps: getTutorialTotalSteps(state),
  currentlyViewedStep: getCurrentlyViewedStep(state),
});

export const AlphaTutorialNotification = connect(mapStateToProps)(AlphaTutorialNotificationImpl);
